import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ZoomIn, ZoomOut, Printer, Maximize2, FileText, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiGetText } from '../../../api/client';
import type { Event } from '../../../api/types';
import type { ContractSection, CustomVariable, DiagnosticCount } from './types';
import { buildLivePreviewHtml } from './helpers';
import DiagnosticsBar from './DiagnosticsBar';

// ── A4 Preview Panel ──────────────────────────────────────────────────────────

const ZOOM_LEVELS = [0.45, 0.6, 0.75, 0.9, 1.0, 1.15, 1.3];

interface PreviewPanelProps {
  sections: ContractSection[];
  customVars: CustomVariable[];
  liveBodyOverrides: Record<string, string>;
  diagnostics: DiagnosticCount;
  contractTitle: string | null;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
}

export default function PreviewPanel({ sections, customVars, liveBodyOverrides, diagnostics, contractTitle, isFullscreen, onToggleFullscreen }: PreviewPanelProps) {
  const [zoomIdx, setZoomIdx] = useState(3);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [eventPreviewHtml, setEventPreviewHtml] = useState<string | null>(null);
  const [eventLoading, setEventLoading] = useState(false);
  const [iframeHeight, setIframeHeight] = useState<number>(0);
  const printFrameRef = useRef<HTMLIFrameElement>(null);

  const zoom = ZOOM_LEVELS[zoomIdx];

  const { data: eventsData } = useQuery<Event[]>({
    queryKey: ['events-list-preview'],
    queryFn: () => apiGet('/events?limit=50&status=PREVENTIVO,CONTRATTO,CONFERMATO,IN_LAVORAZIONE'),
    staleTime: 60_000,
  });
  const events: Event[] = eventsData ?? [];

  // Compute display HTML early (for useEffect deps)
  const sectionsWithOverrides = useMemo(() => sections.map(s => ({
    ...s,
    body: liveBodyOverrides[s.id] ?? s.body,
  })), [sections, liveBodyOverrides]);

  const placeholderHtml = useMemo(
    () => buildLivePreviewHtml(sectionsWithOverrides, customVars, contractTitle),
    [sectionsWithOverrides, customVars, contractTitle],
  );

  const hasLiveOverrides = Object.keys(liveBodyOverrides).length > 0;
  const displayHtml = hasLiveOverrides ? placeholderHtml : (eventPreviewHtml ?? placeholderHtml);

  useEffect(() => {
    if (!selectedEventId) { setEventPreviewHtml(null); return; }
    setEventLoading(true);
    apiGetText(`/events/${selectedEventId}/contract-preview`)
      .then(html => { setEventPreviewHtml(html); setEventLoading(false); })
      .catch(e => { toast.error(e.message); setEventLoading(false); });
  }, [selectedEventId]);

  // Live update iframe on sections/customVars change + auto-height recalc
  useEffect(() => {
    const iframe = printFrameRef.current;
    if (!iframe) return;

    // Recalculate height with polling (200ms debounce)
    const timer = setTimeout(() => {
      const doc = iframe.contentDocument;
      if (doc && doc.body) {
        const h = doc.body.scrollHeight;
        if (h && h > 300) {
          iframe.style.height = `${h + 80}px`;
          setIframeHeight(h);
        }
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [displayHtml]);

  const handlePrint = () => {
    const iframe = printFrameRef.current;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
  };

  const handleOpenInTab = () => {
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(displayHtml);
      w.document.close();
    }
  };

  const fitToWidth = () => setZoomIdx(3);
  const pageCount = iframeHeight > 300 ? Math.ceil(iframeHeight / 1123) : 1;

  return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.375rem',
        padding: '0.5rem 0.875rem',
        borderBottom: '1px solid rgba(255,255,255,0.10)',
        background: 'var(--glass-bg-card)',
        flexShrink: 0, flexWrap: 'wrap',
        overflowX: 'hidden',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <FileText size={14} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
        <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)', flexShrink: 0 }}>Anteprima A4</span>

        <select
          value={selectedEventId}
          onChange={e => setSelectedEventId(e.target.value)}
          style={{
            flex: '1 1 140px', maxWidth: 240,
            padding: '0.28rem 0.5rem', borderRadius: 'var(--radius-interactive)',
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'var(--glass-bg-card)', color: 'var(--color-text)',
            fontSize: 'var(--text-xs)',
          }}
        >
          <option value="">— Placeholder (nessun evento) —</option>
          {events.map(ev => (
            <option key={ev.id} value={ev.id}>
              {ev.title} · {new Date(ev.eventDate).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
            </option>
          ))}
        </select>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', marginLeft: 'auto' }}>
          <button
            onClick={() => setZoomIdx(i => Math.max(0, i - 1))}
            disabled={zoomIdx === 0}
            style={{ background: 'none', border: '1px solid rgba(255,255,255,0.10)', cursor: 'pointer', padding: '0.25rem 0.35rem', borderRadius: 'var(--radius-interactive)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center' }}
            title="Riduci zoom"
          >
            <ZoomOut size={13} />
          </button>
          <button
            onClick={fitToWidth}
            style={{
              background: 'none', border: '1px solid rgba(255,255,255,0.10)',
              cursor: 'pointer', padding: '0.2rem 0.5rem',
              borderRadius: 'var(--radius-interactive)', fontSize: 11,
              color: 'var(--color-text-muted)', minWidth: 44, textAlign: 'center',
            }}
            title="Adatta larghezza"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            onClick={() => setZoomIdx(i => Math.min(ZOOM_LEVELS.length - 1, i + 1))}
            disabled={zoomIdx === ZOOM_LEVELS.length - 1}
            style={{ background: 'none', border: '1px solid rgba(255,255,255,0.10)', cursor: 'pointer', padding: '0.25rem 0.35rem', borderRadius: 'var(--radius-interactive)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center' }}
            title="Aumenta zoom"
          >
            <ZoomIn size={13} />
          </button>
          <button
            onClick={onToggleFullscreen}
            style={{ background: 'none', border: '1px solid rgba(255,255,255,0.10)', cursor: 'pointer', padding: '0.25rem 0.35rem', borderRadius: 'var(--radius-interactive)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center' }}
            title="Schermo intero"
          >
            <Maximize2 size={13} />
          </button>
        </div>

        <button
          onClick={handleOpenInTab}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.3rem',
            padding: '0.3rem 0.5rem',
            background: 'none', border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-interactive)',
            cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)',
          }}
          title="Apri in tab"
        >
          <ExternalLink size={12} />
        </button>

        <button
          onClick={handlePrint}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.3rem',
            padding: '0.3rem 0.7rem',
            background: 'var(--color-primary)', color: 'white',
            border: 'none', borderRadius: 'var(--radius-interactive)',
            cursor: 'pointer', fontSize: 'var(--text-xs)', fontWeight: 600,
          }}
        >
          <Printer size={12} /> Stampa / PDF
        </button>

        {iframeHeight > 300 && (
          <span style={{ marginLeft: '0.5rem', fontSize: 10, color: 'var(--color-text-faint)' }}>
            Pagina ~{pageCount}
          </span>
        )}
      </div>

      {/* Diagnostics bar (only in placeholder mode) */}
      {!selectedEventId && (
        <DiagnosticsBar placeholder={diagnostics.placeholder} missing={diagnostics.missing} />
      )}

      {/* A4 scroll area */}
      <div style={{
        flex: 1, overflow: 'auto', overflowX: 'hidden',
        background: '#d1d5db',
        padding: '1.5rem 0.75rem',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
      }}>
        {eventLoading ? (
          <div style={{ color: 'var(--color-text-muted)', alignSelf: 'center', marginTop: '4rem' }}>
            Caricamento anteprima evento…
          </div>
        ) : (
          <div style={{
            width: 794,
            transform: `scale(${zoom})`,
            transformOrigin: 'top center',
            boxShadow: '0 6px 32px rgba(0,0,0,0.22)',
            borderRadius: 'var(--radius-xs)',
          }}>
            <iframe
              key={displayHtml.length}
              ref={printFrameRef}
              srcDoc={displayHtml}
              title="contract-preview"
              style={{
                width: 794,
                height: '1123px',
                border: 'none',
                display: 'block',
                background: '#fff',
              }}
              onLoad={e => {
                const doc = (e.target as HTMLIFrameElement).contentDocument;
                if (doc) {
                  const h = doc.body?.scrollHeight;
                  if (h && h > 300) {
                    (e.target as HTMLIFrameElement).style.height = `${h + 80}px`;
                  }
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
